import { LoadingView } from "@/components/Loader";
import PdfViewer from "@/components/pdfViewer/PDFViewer";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { downloadInputDocument } from "@/utils/apiCalls";
import { useAuthInfo } from "@propelauth/react";
import React, {
  createContext,
  PropsWithChildren,
  useContext,
  useState,
} from "react";
import { toast } from "sonner";

type Side = "top" | "bottom" | "left" | "right" | null;

type OpenDrawerProps = {
  documentId: string;
  title?: string;
  side?: Side;
};

type DrawerContextProps = {
  openDrawer: (props: OpenDrawerProps) => void;
  closeDrawer: () => void;
};

const DrawerContext = createContext<DrawerContextProps>({
  openDrawer: () => {},
  closeDrawer: () => {},
});

export const DrawerProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const authInfo = useAuthInfo();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [title, setTitle] = useState<string>();
  const [side, setSide] = useState<Side>();

  const fetchDocument = async (documentId: string) => {
    setIsLoading(true);
    const documentResponse = await downloadInputDocument(
      documentId,
      authInfo.accessToken ?? null,
      true
    );
    setIsLoading(false);
    if (documentResponse !== null) {
      setContent(documentResponse);
    } else {
      toast.error("Failed to load document");
    }
  };

  const openDrawer = ({ documentId, title, side }: OpenDrawerProps) => {
    fetchDocument(documentId);
    side && setSide(side);
    title && setTitle(title);
    setIsOpen(true);
  };

  const closeDrawer = () => {
    setIsOpen(false);
    setTimeout(() => setContent(null), 300);
  };

  const handleOpenChange = (state: boolean) => {
    !state && closeDrawer();
  };

  return (
    <DrawerContext.Provider value={{ openDrawer, closeDrawer }}>
      {children}
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side={side}
          className={"flex flex-col sm:max-w-2xl w-2xl"}
        >
          <SheetTitle>{title}</SheetTitle>
          {isLoading ? (
            <LoadingView text="Loading document..." />
          ) : content ? (
            <div className="flex flex-1 items-center justify-center overflow-y-scroll">
              <PdfViewer fileUrl={content} />
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-center overflow-y-scroll">
              <p>No document found</p>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </DrawerContext.Provider>
  );
};

export const useDrawer = () => useContext(DrawerContext);
